import { z } from 'zod';
import {
  isoToLocalInput,
  localInputToIso,
  paiseToRupees,
  percentToBps,
  rupeesToPaise,
  bpsToPercent,
} from '../lib/discount';
import type { CouponDetail, CouponWriteFields } from '../types';

/**
 * Form values are in admin units (rupees, percent, local datetime) and kept
 * as the raw input strings; conversion to wire units happens only in
 * `toWire` — never in the schema — so messages stay in admin terms.
 */
export interface CouponFormValues {
  code: string;
  label: string;
  discountType: 'FLAT' | 'PERCENTAGE';
  /** Rupees for FLAT, percent for PERCENTAGE. */
  amount: string;
  maxDiscountRupees: string;
  startAt: string;
  endAt: string;
  usageLimitGlobal: string;
  usageLimitPerUser: string;
  scope: 'ALL' | 'SELECTED';
  collectionCodes: string[];
}

const num = (v: string) => (v.trim() === '' ? NaN : Number(v));
const isBlank = (v: string) => v.trim() === '';
const isPositiveInt = (v: string) => Number.isInteger(num(v)) && num(v) >= 1;
const MAX_RUPEES = 1_000_000;
/** Paise and basis points are both value × 100, so both need ≤ 2 decimals to stay integers. */
const hasAtMost2Decimals = (n: number) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;

const baseSchema = z.object({
  code: z.string(),
  label: z.string().trim().min(1, 'Label is required').max(200, 'Label must be 200 characters or fewer'),
  discountType: z.enum(['FLAT', 'PERCENTAGE']),
  amount: z.string(),
  maxDiscountRupees: z.string(),
  startAt: z.string().min(1, 'Start date is required'),
  endAt: z.string().min(1, 'End date is required'),
  usageLimitGlobal: z.string(),
  usageLimitPerUser: z.string(),
  scope: z.enum(['ALL', 'SELECTED']),
  collectionCodes: z.array(z.string()).max(100, 'At most 100 products'),
});

function refineShared(v: z.infer<typeof baseSchema>, ctx: z.RefinementCtx) {
  const amount = num(v.amount);
  if (v.discountType === 'FLAT') {
    if (!(amount > 0)) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Enter an amount above ₹0' });
    else if (amount > MAX_RUPEES) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Amount is too large' });
    else if (!hasAtMost2Decimals(amount))
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'At most 2 decimal places' });
  } else {
    // Backend rejects > 10000 bps too (Coupon entity); this is the inline feedback.
    if (!(amount > 0)) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Enter a percentage above 0' });
    else if (amount > 100) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Percentage cannot exceed 100%' });
    else if (!hasAtMost2Decimals(amount))
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'At most 2 decimal places' });
    // No free-order path yet (BUG-04): the backend rejects a 100% coupon unless a cap keeps the price above ₹1.
    if (amount >= 100 && isBlank(v.maxDiscountRupees))
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: '100% (free) coupons are not supported yet — use a lower percentage or add a maximum discount',
      });
    if (!isBlank(v.maxDiscountRupees) && !(num(v.maxDiscountRupees) > 0))
      ctx.addIssue({ code: 'custom', path: ['maxDiscountRupees'], message: 'Enter a cap above ₹0, or leave blank' });
  }
  if (v.startAt && v.endAt && new Date(v.endAt).getTime() <= new Date(v.startAt).getTime())
    ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'End must be after start' });
  for (const key of ['usageLimitGlobal', 'usageLimitPerUser'] as const) {
    if (!isBlank(v[key]) && !isPositiveInt(v[key]))
      ctx.addIssue({ code: 'custom', path: [key], message: 'Whole number of 1 or more, or blank for unlimited' });
  }
  if (v.scope === 'SELECTED' && v.collectionCodes.length === 0)
    ctx.addIssue({ code: 'custom', path: ['collectionCodes'], message: 'Select at least one product, or choose "All products"' });
}

export const createCouponSchema = baseSchema
  .extend({
    code: z
      .string()
      .regex(/^[A-Z0-9_]{4,32}$/, '4–32 characters: uppercase letters, numbers, underscore'),
  })
  .superRefine(refineShared);

/** No `code` rule on edit — it is immutable and not part of the update DTO. */
export const editCouponSchema = baseSchema.superRefine(refineShared);

const intOrNull = (v: string) => (isBlank(v) ? null : Number(v));

export function toWire(v: CouponFormValues): CouponWriteFields {
  const amount = num(v.amount);
  const isPct = v.discountType === 'PERCENTAGE';
  return {
    label: v.label.trim(),
    discountType: v.discountType,
    discountValue: isPct ? percentToBps(amount) : rupeesToPaise(amount),
    // Backend rejects maxDiscountPaise on FLAT, so it is always null there.
    maxDiscountPaise: isPct && !isBlank(v.maxDiscountRupees) ? rupeesToPaise(num(v.maxDiscountRupees)) : null,
    startAt: localInputToIso(v.startAt),
    endAt: localInputToIso(v.endAt),
    usageLimitGlobal: intOrNull(v.usageLimitGlobal),
    usageLimitPerUser: intOrNull(v.usageLimitPerUser),
    collectionCodes: v.scope === 'ALL' ? [] : [...v.collectionCodes].sort(),
  };
}

export function toFormValues(c: CouponDetail): CouponFormValues {
  const isPct = c.discountType === 'PERCENTAGE';
  return {
    code: c.code,
    label: c.label,
    discountType: c.discountType,
    amount: String(isPct ? bpsToPercent(c.discountValue) : paiseToRupees(c.discountValue)),
    maxDiscountRupees: c.maxDiscountPaise === null ? '' : String(paiseToRupees(c.maxDiscountPaise)),
    startAt: isoToLocalInput(c.startAt),
    endAt: isoToLocalInput(c.endAt),
    usageLimitGlobal: c.usageLimitGlobal === null ? '' : String(c.usageLimitGlobal),
    usageLimitPerUser: c.usageLimitPerUser === null ? '' : String(c.usageLimitPerUser),
    scope: c.collectionCodes.length === 0 ? 'ALL' : 'SELECTED',
    collectionCodes: [...c.collectionCodes],
  };
}

export const EMPTY_FORM: CouponFormValues = {
  code: '',
  label: '',
  discountType: 'FLAT',
  amount: '',
  maxDiscountRupees: '',
  startAt: '',
  endAt: '',
  usageLimitGlobal: '',
  usageLimitPerUser: '',
  scope: 'ALL',
  collectionCodes: [],
};

/**
 * Only the fields that actually changed. Both sides go through `toWire`, so
 * minute-truncated dates and rounded amounts compare equal when untouched —
 * sending an unchanged pricing field on a used coupon would still 409.
 */
export function diffWire(original: CouponWriteFields, next: CouponWriteFields): Partial<CouponWriteFields> {
  const out: Partial<CouponWriteFields> = {};
  for (const key of Object.keys(next) as (keyof CouponWriteFields)[]) {
    if (JSON.stringify(original[key]) !== JSON.stringify(next[key])) {
      (out as Record<string, unknown>)[key] = next[key];
    }
  }
  return out;
}
